import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import SpeakersList from "~/TTS/SpeakersList";
import { extractInfo } from "~/TTS/SpeakersList";
import {
  T2SEngineAtom,
  currentSpeakerAtom,
  languageAtom,
  volumeAtom,
  voiceSpeedAtom,
  voicePitchAtom,
  voiceIntonationAtom,
} from "~/Atom";
import * as db from "~/components/db/CharacterDBUtils";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import { VRM } from "@pixiv/three-vrm";
import { PlaySound } from "~/SoundUtils";
import "~/css/SpeakerSelector.css";
import "~/css/RadioButton.css";
import { currentLocale, t2SEngines, getNameAndStyle } from "~/Commons";

interface SpeakerSelectorProps {
  loadedVrm: VRM | null;
}

const SpeakerSelector: React.FC<SpeakerSelectorProps> = ({ loadedVrm }) => {
  const { t } = useTranslation();
  const [selectedEngine, setSelectedEngine] = useAtom(T2SEngineAtom);
  const [, setCurrentSpeaker] = useAtom(currentSpeakerAtom);
  const [language] = useAtom(languageAtom);
  const [volume] = useAtom(volumeAtom);
  const [, setSpeed] = useAtom(voiceSpeedAtom);
  const [, setPitch] = useAtom(voicePitchAtom);
  const [, setIntonation] = useAtom(voiceIntonationAtom);

  /**
   * 選択されたエンジンに基づいてスピーカーの読み込みを行う
   */
  const updateCurrentSpeaker = (engine: string): void => {
    // currentVRMが空の場合は処理をスキップする
    if (!loadedVrm) return;

    // キャラクターによって初期値を取得して適用
    const currentCharacter = getVRMName(loadedVrm);
    switch (engine) {
      case t2SEngines.voicevox:
        // eslint-disable-next-line complexity
        db.getVoicevoxConfig(currentCharacter).then((config) => {
          setSpeed(config?.speed ?? 1.0);
          setPitch(config?.pitch ?? 0.0);
          setIntonation(config?.intonation ?? 1.0);
          const speaker = extractInfo(engine, config?.speaker_id ?? 0);
          const { name, style } = getNameAndStyle(engine, speaker);
          setCurrentSpeaker({
            Id: config?.speaker_id ?? 0,
            Name: name ?? "",
            Style: style ?? "",
          });
        });
        break;
      case t2SEngines.openai:
        db.getOpenaiConfig(currentCharacter).then((config) => {
          const speaker = extractInfo(engine, config?.speaker_id ?? 0);
          const { name, style } = getNameAndStyle(engine, speaker);
          setCurrentSpeaker({
            Id: config?.speaker_id ?? 0,
            Name: name ?? "",
            Style: style ?? "",
          });
        });
        break;
      case t2SEngines.aivisSpeech:
        // eslint-disable-next-line complexity
        db.getAivisSpeechConfig(currentCharacter).then((config) => {
          setSpeed(config?.speed ?? 1.0);
          setPitch(config?.pitch ?? 0.0);
          setIntonation(config?.intonation ?? 1.0);
          const speaker = extractInfo(engine, config?.speaker_id ?? 0);
          const { name, style } = getNameAndStyle(engine, speaker);
          setCurrentSpeaker({
            Id: config?.speaker_id ?? 0,
            Name: name ?? "",
            Style: style ?? "",
          });
        });
        break;
    }
  };

  /**
   * 音声エンジン変更イベントの処理
   */
  const handleServiceChange = async (event: {
    target: { value: React.SetStateAction<string> };
  }): Promise<void> => {
    const engineName = event.target.value as string;
    setSelectedEngine(engineName);

    // 現在のキャラクター名を取得し、DB更新
    const currentCharacter = getVRMName(loadedVrm);
    // 更新するカラム名を決定
    const column =
      process.env.REACT_APP_SYSTEM_REQUIREMENT === "onprem" ? "tts_engine" : "ttsEngine";
    await db.update(currentCharacter, column, engineName);
    PlaySound("push2", volume);
  };

  useEffect(() => {
    updateCurrentSpeaker(selectedEngine);
  }, [selectedEngine, loadedVrm]);

  return (
    <div>
      <div className="speaker-selector">
        <div className="title font-default-normal-20">{t("selectCV")}</div>
        <div className="engine-container">
          <form className="engine-list">
            <div className="radio-row">
              <label className="custom-radio-label">
                <input
                  className="custom-radio"
                  type="radio"
                  value={t2SEngines.voicevox}
                  checked={selectedEngine === t2SEngines.voicevox}
                  onChange={handleServiceChange}
                  name="engine"
                  id="voicevoxRadio"
                  disabled={language !== currentLocale}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">VOICEVOX</span>
              </label>
            </div>
            <div className="radio-row">
              <label className="custom-radio-label">
                <input
                  className="custom-radio"
                  type="radio"
                  value={t2SEngines.aivisSpeech}
                  checked={selectedEngine === t2SEngines.aivisSpeech}
                  onChange={handleServiceChange}
                  name="engine"
                  id="AivisSpeechRadio"
                  disabled={language !== currentLocale}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">AivisSpeech</span>
              </label>
            </div>
            <div className="radio-row">
              <label className="custom-radio-label">
                <input
                  className="custom-radio"
                  type="radio"
                  value={t2SEngines.openai}
                  checked={selectedEngine === t2SEngines.openai}
                  onChange={handleServiceChange}
                  name="engine"
                  id="OpenAIRadio"
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">OpenAI</span>
              </label>
            </div>
          </form>

          <SpeakersList />
        </div>
      </div>
    </div>
  );
};

export default SpeakerSelector;
